import { useEffect, useRef, useState } from 'react';

import formatTime from '@cartwhl/common/utils/formatTime';
import Text from '@cartwhl/ui/base/Text';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';

const FPS = 24; // The custom frame update loop will run at this fps

export default function CustomVideoPlayer({
  src,
  mimeType,
  startTime,
  endTime,
  duration,
  className,
  currentTime,
  isPlaying,
  setIsPlaying,
  onTimeUpdate,
  objectFit = 'cover',
  showPlayButton = true,
}: {
  src: string;
  mimeType: string;
  startTime: number;
  endTime: number;
  duration: number;
  className?: string;
  currentTime: number;
  isPlaying: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
  onTimeUpdate: (time: number) => void;
  objectFit?: 'cover' | 'contain';
  showPlayButton?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Load the video explicitly
    video.load();

    const handleLoaded = () => {
      // Reduce readyState requirement to HAVE_METADATA (1)
      if (video.readyState >= 1) {
        setIsLoaded(true);
      }
    };

    // Check initial state
    handleLoaded();

    // Add error handling
    const handleError = (e: ErrorEvent) => {
      console.error('Video loading error:', e);
    };

    video.addEventListener('error', handleError);
    video.addEventListener('loadeddata', handleLoaded);

    return () => {
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadeddata', handleLoaded);
    };
  }, [src]); // Add src as dependency to reload when source changes

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isLoaded) return;

    const validStartTime = Number.isFinite(startTime)
      ? Math.max(0, startTime)
      : 0;
    const validEndTime = Number.isFinite(endTime)
      ? Math.min(endTime, duration)
      : duration;

    let lastUpdateTime = performance.now();

    const updateFrame = () => {
      const now = performance.now();
      const deltaTime = (now - lastUpdateTime) / 1000;
      lastUpdateTime = now;

      if (isPlaying) {
        const newTime = video.currentTime + deltaTime;

        if (newTime >= validEndTime) {
          video.currentTime = validStartTime;
          onTimeUpdate(0);
        } else {
          video.currentTime = newTime;
          onTimeUpdate(video.currentTime - validStartTime);
        }
      }
    };

    // Initial setup
    if (!isPlaying) {
      video.currentTime = validStartTime + currentTime;
    }

    // Set up interval for regular updates
    const intervalId = setInterval(updateFrame, 1000 / FPS);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    isPlaying,
    startTime,
    endTime,
    duration,
    isLoaded,
    currentTime,
    onTimeUpdate,
  ]);

  const togglePlay = () => {
    if (!isLoaded) return;
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="group relative h-full w-full">
      <video
        ref={videoRef}
        playsInline
        muted
        preload="auto"
        crossOrigin="anonymous"
        className={`w-full ${className}`}
        style={{ objectFit }}
      >
        <source src={src} type={mimeType} />
      </video>
      {showPlayButton && (
        <button
          onClick={togglePlay}
          className={`absolute inset-4 flex items-end justify-center transition-opacity`}
          disabled={!isLoaded}
        >
          <div className="bg-gray-90/50 flex h-[36px] items-center gap-1 rounded-full px-4 text-white lg:gap-2">
            {!isLoaded ? (
              <Text
                variant="studio/body/medium/xs"
                lg:variant="studio/body/medium/sm"
                className="text-white"
              >
                Loading...
              </Text>
            ) : isPlaying ? (
              <PauseIcon className="text-white" />
            ) : (
              <PlayIcon className="text-white" />
            )}
            <Text
              variant="studio/body/medium/xs"
              lg:variant="studio/body/medium/sm"
              className="min-w-[32px] pt-[3px] text-right"
            >
              {formatTime(Math.max(0, currentTime))}
            </Text>
            <Text
              variant="studio/body/medium/xs"
              lg:variant="studio/body/medium/sm"
              className="pt-[3px]"
            >
              /
            </Text>
            <Text
              variant="studio/body/medium/xs"
              lg:variant="studio/body/medium/sm"
              className="pt-[3px]"
            >
              {formatTime(endTime - startTime)}
            </Text>
          </div>
        </button>
      )}
    </div>
  );
}
